import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Play,
  Pause,
  Pencil,
  Trash2,
  Clock,
  Loader2,
} from "lucide-react";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
interface Automation {
  id: string;
  title: string;
  definition: string;
  kind: string;
  reminder?: { target: string; taskId?: string };
  projectIds: string[];
  modelId?: string | null;
  schedule: {
    frequency: string;
    time?: string;
    at?: string;
    weekdays?: number[];
  };
  timezone: string;
  delivery: string;
  permissionMode: string;
  enabled: boolean;
  version: number;
  nextRunAt: string | null;
  executions: {
    id: string;
    status: string;
    createdAt: string;
    error?: { message: string };
    runId?: string;
  }[];
}
const selectClass =
  "w-full rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm";
export function AssistantAutomations({ onBudget }: { onBudget(): void }) {
  const fr = useI18n().language === "fr";
  const client = useQueryClient();
  const [editing, setEditing] = useState<Automation | null | undefined>();
  const list = useQuery({
    queryKey: ["assistant-automations"],
    queryFn: () =>
      apiGet<{ automations: Automation[] }>("/ai/assistant/automations"),
    refetchInterval: 5000,
  });
  const budget = useQuery({
    queryKey: ["assistant-budget"],
    queryFn: () =>
      apiGet<{
        period: string;
        used: number;
        reserved: number;
        limit: number | null;
      }>("/ai/assistant/budget"),
    refetchInterval: 5000,
  });
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ["assistant-automations"] });
    void client.invalidateQueries({ queryKey: ["ai-history"] });
  };
  const action = useMutation({
    mutationFn: ({ item, action }: { item: Automation; action: string }) =>
      action === "delete"
        ? apiDelete(`/ai/assistant/automations/${item.id}`)
        : action === "run"
          ? apiPost(`/ai/assistant/automations/${item.id}/run`, {
              requestId: crypto.randomUUID(),
            })
          : apiPatch(`/ai/assistant/automations/${item.id}`, {
              title: item.title,
              definition: item.definition,
              kind: item.kind,
              projectIds: item.projectIds,
              modelId: item.modelId,
              reminder: item.reminder,
              schedule: item.schedule,
              timezone: item.timezone,
              delivery: item.delivery,
              permissionMode: item.permissionMode,
              enabled: !item.enabled,
              expectedVersion: item.version,
            }),
    onSuccess: refresh,
  });
  const [deleting, setDeleting] = useState<Automation | null>(null);
  return (
    <div className="space-y-5">
      {budget.data && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm text-foreground-secondary flex flex-wrap items-center gap-3">
          <p className="flex-1">
            {fr ? "Budget IA partagé" : "Shared AI budget"} ·{" "}
            {budget.data.period}: {budget.data.used.toLocaleString()} /{" "}
            {budget.data.limit?.toLocaleString() ??
              (fr ? "non défini" : "not set")}{" "}
            tokens
            {budget.data.reserved > 0 &&
              ` · ${budget.data.reserved.toLocaleString()} ${fr ? "réservés" : "reserved"}`}
          </p>
          <Button variant="outline" size="sm" onClick={onBudget}>
            {budget.data.limit == null
              ? fr
                ? "Définir le budget"
                : "Set budget"
              : fr
                ? "Modifier le budget"
                : "Edit budget"}
          </Button>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h2 className="text-lg font-semibold">
            {fr ? "Automatisations" : "Automations"}
          </h2>
          <p className="text-sm text-foreground-muted">
            {fr
              ? "Choisissez les horaires, projets, permissions et destinataires de chaque automatisation."
              : "Choose the schedule, projects, permissions and delivery for each automation."}
          </p>
        </div>
        <Button onClick={() => setEditing(null)} className="gap-2">
          <Plus className="h-4 w-4" />
          {fr ? "Créer" : "Create"}
        </Button>
      </div>
      {action.error && (
        <p role="alert" className="text-sm text-destructive">
          {action.error.message}
        </p>
      )}
      {list.error && (
        <p role="alert" className="text-sm text-destructive">
          {list.error.message}
        </p>
      )}
      {list.data?.automations.length === 0 && (
        <p className="py-12 text-center text-foreground-muted">
          {fr
            ? "Aucune automatisation. Vous contrôlez ce qui s’exécute et quand."
            : "No automations yet. You control what runs and when."}
        </p>
      )}
      {list.data?.automations.map((item) => (
        <article
          key={item.id}
          className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3"
        >
          <div className="flex gap-3">
            <Clock className="mt-1 h-5 w-5 text-primary" />
            <div className="min-w-0 flex-1">
              <h3 className="font-medium">{item.title}</h3>
              <p className="text-sm text-foreground-muted whitespace-pre-wrap">
                {item.kind === "REMINDER"
                  ? item.reminder?.target === "TASK"
                    ? fr
                      ? "Suivi d’une tâche"
                      : "Task follow-up"
                    : fr
                      ? "Rappel des échéances, retards et tâches planifiées"
                      : "Due, overdue and planned tasks"
                  : item.definition}
              </p>
            </div>
            <span className="text-xs text-foreground-muted">
              {item.enabled
                ? fr
                  ? "Activée"
                  : "Enabled"
                : fr
                  ? "En pause"
                  : "Paused"}
            </span>
          </div>
          <p className="text-xs text-foreground-muted">
            {
              (
                {
                  READ_ONLY: fr ? "Lecture seule" : "Read only",
                  ASK: fr ? "Approbation requise" : "Approval required",
                  EDIT: fr ? "Modifications automatiques" : "Automatic edits",
                  FULL: fr ? "Accès complet" : "Full access",
                } as Record<string, string>
              )[item.permissionMode]
            }{" "}
            ·{" "}
            {
              (
                {
                  WEBSITE: fr ? "Site web" : "Website",
                  DISCORD: "Discord",
                  BOTH: fr ? "Site et Discord" : "Website and Discord",
                } as Record<string, string>
              )[item.delivery]
            }{" "}
            · {item.timezone}
            {item.nextRunAt &&
              ` · ${fr ? "Prochaine" : "Next"}: ${new Date(item.nextRunAt).toLocaleString()}`}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={action.isPending}
              onClick={() => action.mutate({ item, action: "toggle" })}
            >
              {item.enabled ? (
                <Pause className="mr-2 h-3 w-3" />
              ) : (
                <Play className="mr-2 h-3 w-3" />
              )}
              {item.enabled
                ? fr
                  ? "Pause"
                  : "Pause"
                : fr
                  ? "Activer"
                  : "Enable"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={action.isPending}
              onClick={() => action.mutate({ item, action: "run" })}
            >
              <Play className="mr-2 h-3 w-3" />
              {fr ? "Exécuter" : "Run now"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(item)}>
              <Pencil className="mr-2 h-3 w-3" />
              {fr ? "Modifier" : "Edit"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDeleting(item)}>
              <Trash2 className="mr-2 h-3 w-3" />
              {fr ? "Supprimer" : "Delete"}
            </Button>
          </div>
          {item.executions.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs text-foreground-muted">
                {fr ? "Dernières exécutions" : "Recent executions"}
              </summary>
              <ul className="mt-2 space-y-1 text-xs">
                {item.executions.map((execution) => (
                  <li key={execution.id}>
                    {new Date(execution.createdAt).toLocaleString()} ·{" "}
                    {execution.status}
                    {execution.error && (
                      <span className="text-red-300">
                        {" "}
                        · {execution.error.message}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </article>
      ))}
      {editing !== undefined && (
        <AutomationEditor
          item={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            refresh();
          }}
        />
      )}
      <Dialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {fr ? "Supprimer l’automatisation ?" : "Delete automation?"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm">{deleting?.title}</p>
          <p className="text-sm text-foreground-muted">
            {fr
              ? "Les exécutions déjà lancées gardent leur politique. L’historique est conservé."
              : "Already-started executions keep their policy. History is preserved."}
          </p>
          <Button
            variant="destructive"
            disabled={action.isPending}
            onClick={() => {
              if (deleting)
                action.mutate(
                  { item: deleting, action: "delete" },
                  { onSuccess: () => setDeleting(null) },
                );
            }}
          >
            {fr ? "Supprimer" : "Delete"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function AutomationEditor({
  item,
  onClose,
  onSaved,
}: {
  item: Automation | null;
  onClose(): void;
  onSaved(): void;
}) {
  const fr = useI18n().language === "fr";
  const [title, setTitle] = useState(item?.title ?? "");
  const [definition, setDefinition] = useState(item?.definition ?? "");
  const [kind, setKind] = useState(item?.kind ?? "REMINDER");
  const [reminderTarget, setReminderTarget] = useState(
    item?.reminder?.target ?? "TODAY",
  );
  const [reminderTask, setReminderTask] = useState(
    item?.reminder?.taskId ?? "",
  );
  const [ids, setIds] = useState(item?.projectIds ?? []);
  const [model, setModel] = useState(item?.modelId ?? "");
  const [frequency, setFrequency] = useState(
    item?.schedule.frequency ?? "DAILY",
  );
  const [time, setTime] = useState(item?.schedule.time ?? "");
  const [at, setAt] = useState(
    item?.schedule.at ? wallTime(item.schedule.at, item.timezone) : "",
  );
  const [weekdays, setWeekdays] = useState(item?.schedule.weekdays ?? []);
  const [timezone, setTimezone] = useState(
    item?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [delivery, setDelivery] = useState(item?.delivery ?? "");
  const [permission, setPermission] = useState(
    item?.permissionMode ?? "READ_ONLY",
  );
  const projects = useQuery({
    queryKey: ["assistant-projects"],
    queryFn: () =>
      apiGet<{ projects: { id: string; name: string; role: string }[] }>(
        "/ai/assistant/projects",
      ),
  });
  const providers = useQuery({
    queryKey: ["ai-providers"],
    queryFn: () =>
      apiGet<{
        connections: {
          label: string;
          enabled: boolean;
          modelConfigs: { id: string; modelId: string; enabled: boolean }[];
        }[];
      }>("/ai/providers"),
  });
  const references = useQuery({
    queryKey: ["assistant-reminder-references"],
    queryFn: () =>
      apiGet<{
        tasks: {
          id: string;
          title: string;
          projectId: string;
          projectLabel: string;
        }[];
      }>("/ai/assistant/references"),
    enabled: kind === "REMINDER" && reminderTarget === "TASK",
  });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        title,
        definition:
          kind === "REMINDER"
            ? reminderTarget === "TODAY"
              ? "Due, overdue and planned tasks"
              : "Follow up on the selected task"
            : definition,
        kind,
        ...(kind === "REMINDER"
          ? {
              reminder: {
                target: reminderTarget,
                ...(reminderTarget === "TASK" ? { taskId: reminderTask } : {}),
              },
            }
          : {}),
        projectIds: ids,
        modelId: kind === "REMINDER" ? null : model,
        schedule:
          frequency === "ONCE"
            ? { frequency, atLocal: at }
            : {
                frequency,
                time,
                ...(frequency === "WEEKLY" ? { weekdays } : {}),
              },
        timezone,
        delivery,
        permissionMode: kind === "REMINDER" ? "READ_ONLY" : permission,
        enabled: item?.enabled ?? false,
        ...(item ? { expectedVersion: item.version } : {}),
      };
      return item
        ? apiPatch(`/ai/assistant/automations/${item.id}`, body)
        : apiPost("/ai/assistant/automations", body);
    },
    onSuccess: onSaved,
  });
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) onClose();
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {fr ? "Configurer l’automatisation" : "Configure automation"}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!save.isPending) save.mutate();
          }}
        >
          <label className="block text-sm space-y-2">
            <span>{fr ? "Nom" : "Name"}</span>
            <Input
              required
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <label className="block text-sm space-y-2">
            <span>{fr ? "Travail à effectuer" : "Work to perform"}</span>
            <Textarea
              required={kind !== "REMINDER"}
              disabled={kind === "REMINDER"}
              value={
                kind === "REMINDER"
                  ? fr
                    ? "Rappel des tâches de la sélection ci-dessous, sans appel IA."
                    : "Reminder for the selection below, without an AI call."
                  : definition
              }
              onChange={(event) => setDefinition(event.target.value)}
              maxLength={6000}
            />
          </label>
          <label className="block text-sm space-y-2">
            <span>{fr ? "Type" : "Type"}</span>
            <select
              className={selectClass}
              value={kind}
              onChange={(event) => setKind(event.target.value)}
            >
              <option value="REMINDER">
                {fr
                  ? "Rappel déterministe (sans IA)"
                  : "Deterministic reminder (no AI)"}
              </option>
              <option value="REPORT">{fr ? "Rapport IA" : "AI report"}</option>
              <option value="WORKFLOW">
                {fr ? "Travail IA" : "AI workflow"}
              </option>
            </select>
          </label>
          <fieldset className="rounded-xl border border-white/10 p-3 space-y-2">
            <legend className="px-1 text-sm">
              {fr ? "Projets accessibles" : "Accessible projects"}
            </legend>
            {projects.data?.projects.map((project) => (
              <label
                key={project.id}
                className="flex items-center gap-2 text-sm"
              >
                <Checkbox
                  checked={ids.includes(project.id)}
                  onCheckedChange={(value) =>
                    setIds((current) =>
                      value
                        ? [...current, project.id]
                        : current.filter((id) => id !== project.id),
                    )
                  }
                />
                {project.name}
                <span className="ml-auto text-xs text-foreground-muted">
                  {project.role}
                </span>
              </label>
            ))}
          </fieldset>
          {kind === "REMINDER" && (
            <>
              <label className="block text-sm space-y-2">
                <span>
                  {fr ? "Tâches à rappeler" : "Tasks to remind you of"}
                </span>
                <select
                  className={selectClass}
                  value={reminderTarget}
                  onChange={(event) => setReminderTarget(event.target.value)}
                >
                  <option value="TODAY">
                    {fr
                      ? "Échéances, retards et tâches planifiées aujourd’hui"
                      : "Due, overdue and planned today"}
                  </option>
                  <option value="TASK">
                    {fr ? "Suivre une tâche précise" : "Follow up on one task"}
                  </option>
                </select>
              </label>
              {reminderTarget === "TASK" && (
                <select
                  required
                  className={selectClass}
                  aria-label={fr ? "Tâche" : "Task"}
                  value={reminderTask}
                  onChange={(event) => setReminderTask(event.target.value)}
                >
                  <option value="">
                    {fr ? "Choisir une tâche" : "Choose task"}
                  </option>
                  {references.data?.tasks
                    .filter((task) => ids.includes(task.projectId))
                    .map((task) => (
                      <option key={task.id} value={task.id}>
                        {task.projectLabel} · {task.title}
                      </option>
                    ))}
                </select>
              )}
            </>
          )}
          {kind !== "REMINDER" && (
            <>
              <label className="block text-sm space-y-2">
                <span>{fr ? "Modèle personnel" : "Personal model"}</span>
                <select
                  className={selectClass}
                  required
                  value={model}
                  onChange={(event) => setModel(event.target.value)}
                >
                  <option value="">{fr ? "Choisir" : "Choose"}</option>
                  {providers.data?.connections
                    .filter((connection) => connection.enabled)
                    .flatMap((connection) =>
                      connection.modelConfigs
                        .filter((model) => model.enabled)
                        .map((model) => (
                          <option key={model.id} value={model.id}>
                            {connection.label} · {model.modelId}
                          </option>
                        )),
                    )}
                </select>
              </label>
              <label className="block text-sm space-y-2">
                <span>
                  {fr
                    ? "Permissions de cette automatisation"
                    : "Permissions for this automation"}
                </span>
                <select
                  className={selectClass}
                  value={permission}
                  onChange={(event) => setPermission(event.target.value)}
                >
                  <option value="READ_ONLY">
                    {fr ? "Lecture seule" : "Read only"}
                  </option>
                  <option value="ASK">
                    {fr ? "Approbation requise" : "Approval required"}
                  </option>
                  <option value="EDIT">
                    {fr
                      ? "Modifications automatiques non destructives"
                      : "Automatic non-destructive edits"}
                  </option>
                  <option value="FULL">
                    {fr
                      ? "Accès complet, y compris destructif"
                      : "Full access, including destructive actions"}
                  </option>
                </select>
              </label>
            </>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm space-y-2">
              <span>{fr ? "Répétition" : "Frequency"}</span>
              <select
                className={selectClass}
                value={frequency}
                onChange={(event) => setFrequency(event.target.value)}
              >
                <option value="DAILY">{fr ? "Chaque jour" : "Daily"}</option>
                <option value="WEEKLY">
                  {fr ? "Chaque semaine" : "Weekly"}
                </option>
                <option value="ONCE">{fr ? "Une fois" : "Once"}</option>
              </select>
            </label>
            <label className="text-sm space-y-2">
              <span>{fr ? "Horaire" : "Time"}</span>
              <Input
                required
                type={frequency === "ONCE" ? "datetime-local" : "time"}
                value={frequency === "ONCE" ? at : time}
                onChange={(event) =>
                  frequency === "ONCE"
                    ? setAt(event.target.value)
                    : setTime(event.target.value)
                }
              />
            </label>
          </div>
          {frequency === "WEEKLY" && (
            <div className="flex flex-wrap gap-3">
              {Array.from({ length: 7 }, (_, day) => (
                <label key={day} className="flex items-center gap-1 text-sm">
                  <Checkbox
                    checked={weekdays.includes(day)}
                    onCheckedChange={(value) =>
                      setWeekdays((current) =>
                        value
                          ? [...current, day]
                          : current.filter((value) => value !== day),
                      )
                    }
                  />
                  {new Date(Date.UTC(2026, 0, 4 + day)).toLocaleDateString(
                    fr ? "fr" : "en",
                    { weekday: "short", timeZone: "UTC" },
                  )}
                </label>
              ))}
            </div>
          )}
          <label className="block text-sm space-y-2">
            <span>{fr ? "Fuseau horaire" : "Timezone"}</span>
            <Input
              required
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
            />
          </label>
          <label className="block text-sm space-y-2">
            <span>{fr ? "Livraison" : "Delivery"}</span>
            <select
              className={selectClass}
              required
              value={delivery}
              onChange={(event) => setDelivery(event.target.value)}
            >
              <option value="">
                {fr ? "Choisir explicitement" : "Choose explicitly"}
              </option>
              <option value="WEBSITE">{fr ? "Site web" : "Website"}</option>
              <option value="DISCORD">Discord</option>
              <option value="BOTH">
                {fr ? "Site et Discord" : "Website and Discord"}
              </option>
            </select>
          </label>
          <p className="text-xs text-foreground-muted">
            {fr
              ? "Une nouvelle automatisation est enregistrée en pause. Activez-la après vérification des réglages."
              : "New automations are saved paused. Enable after reviewing the settings."}
          </p>
          {save.error && (
            <p role="alert" className="text-sm text-destructive">
              {save.error.message}
            </p>
          )}
          <Button
            type="submit"
            disabled={
              save.isPending ||
              !ids.length ||
              (frequency === "WEEKLY" && !weekdays.length)
            }
          >
            {save.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {fr ? "Enregistrer" : "Save"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function wallTime(value: string, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  const part = (name: string) =>
    parts.find((item) => item.type === name)!.value;
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
}
